<script>
  import { onMount, onDestroy } from 'svelte';
  import { getVoucherAnalytics, authFetch, getVoucherTitles, getVoucherCategories } from '../lib/api.js';
  import { showAlert } from '../lib/stores.js';
  import { navigate } from 'svelte-routing';

  // State variables
  let loading = true;
  let analytics = null;
  let error = null;
  let chartType = 'bar'; // 'bar' or 'pie'
  
  // Filter variables
  let titles = [];
  let categories = [];
  let selectedTitleId = '';
  let selectedCategoryId = '';
  let loadingFilters = false;
  
  // Chart dimensions
  const width = 800;
  const height = 400;
  const margin = { top: 20, right: 20, bottom: 30, left: 40 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  // Colors for charts
  const colors = {
    used: '#4ade80', // green
    unused: '#f87171', // red
    bar: '#3b82f6', // blue
    pie: [
      '#3b82f6', // blue
      '#8b5cf6', // purple
      '#ec4899', // pink
      '#f97316', // orange
      '#84cc16', // lime
      '#06b6d4', // cyan
      '#6366f1', // indigo
      '#14b8a6', // teal
      '#f43f5e', // rose
      '#10b981'  // emerald
    ]
  };
  
  // Format numbers with thousand separators
  function formatNumber(num) {
    return new Intl.NumberFormat('id-ID').format(num);
  }
  
  // Format percentage
  function formatPercentage(num) {
    return `${Math.round(num)}%`;
  }
  
  // Draw bar chart for category data
  function drawCategoryChart(container, data) {
    // Clear previous chart
    container.innerHTML = '';
    
    if (!data || data.length === 0) {
      container.innerHTML = '<div class="text-center text-gray-400">Tidak ada data kategori</div>';
      return;
    }
    
    if (chartType === 'bar') {
      drawCategoryBarChart(container, data);
    } else {
      drawCategoryPieChart(container, data);
    }
  }
  
  // Bar chart for categories
  function drawCategoryBarChart(container, data) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    container.appendChild(svg);
    
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(${margin.left},${margin.top})`);
    svg.appendChild(g);
    
    // Prepare data
    const sortedData = [...data].sort((a, b) => b.total - a.total);
    const maxValue = Math.max(...sortedData.map(d => d.total));
    
    // X scale - categories
    const xScale = sortedData.map((d, i) => ({
      value: d.categoryName,
      position: i * (innerWidth / sortedData.length)
    }));
    
    // Y scale - voucher counts
    const yScale = value => (innerHeight * (1 - value / maxValue));
    
    // Draw axes
    // X axis
    const xAxis = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    xAxis.setAttribute('transform', `translate(0,${innerHeight})`);
    g.appendChild(xAxis);
    
    // X axis line
    const xAxisLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    xAxisLine.setAttribute('x1', 0);
    xAxisLine.setAttribute('y1', 0);
    xAxisLine.setAttribute('x2', innerWidth);
    xAxisLine.setAttribute('y2', 0);
    xAxisLine.setAttribute('stroke', '#e5e7eb');
    xAxis.appendChild(xAxisLine);
    
    // X axis labels
    xScale.forEach(x => {
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', x.position + (innerWidth / sortedData.length) / 2);
      text.setAttribute('y', 20);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '10px');
      text.setAttribute('fill', '#6b7280');
      text.textContent = x.value.length > 15 ? x.value.substring(0, 15) + '...' : x.value;
      xAxis.appendChild(text);
    });
    
    // Y axis
    const yAxis = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.appendChild(yAxis);
    
    // Y axis line
    const yAxisLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    yAxisLine.setAttribute('x1', 0);
    yAxisLine.setAttribute('y1', 0);
    yAxisLine.setAttribute('x2', 0);
    yAxisLine.setAttribute('y2', innerHeight);
    yAxisLine.setAttribute('stroke', '#e5e7eb');
    yAxis.appendChild(yAxisLine);
    
    // Y axis ticks and labels
    const yTicks = 5;
    for (let i = 0; i <= yTicks; i++) {
      const value = maxValue * (i / yTicks);
      const y = yScale(value);
      
      // Tick line
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      tick.setAttribute('x1', -6);
      tick.setAttribute('y1', y);
      tick.setAttribute('x2', 0);
      tick.setAttribute('y2', y);
      tick.setAttribute('stroke', '#e5e7eb');
      yAxis.appendChild(tick);
      
      // Grid line
      const gridLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      gridLine.setAttribute('x1', 0);
      gridLine.setAttribute('y1', y);
      gridLine.setAttribute('x2', innerWidth);
      gridLine.setAttribute('y2', y);
      gridLine.setAttribute('stroke', '#f3f4f6');
      gridLine.setAttribute('stroke-dasharray', '2,2');
      g.appendChild(gridLine);
      
      // Label
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', -10);
      text.setAttribute('y', y);
      text.setAttribute('text-anchor', 'end');
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('font-size', '10px');
      text.setAttribute('fill', '#6b7280');
      text.textContent = formatNumber(Math.round(value));
      yAxis.appendChild(text);
    }
    
    // Draw bars
    sortedData.forEach((d, i) => {
      const barWidth = (innerWidth / sortedData.length) * 0.8;
      const barX = xScale[i].position + ((innerWidth / sortedData.length) - barWidth) / 2;
      
      // Draw used vouchers bar
      const usedHeight = innerHeight - yScale(d.used);
      const usedBar = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      usedBar.setAttribute('x', barX);
      usedBar.setAttribute('y', innerHeight - usedHeight);
      usedBar.setAttribute('width', barWidth);
      usedBar.setAttribute('height', usedHeight);
      usedBar.setAttribute('fill', colors.used);
      usedBar.setAttribute('rx', 2);
      
      // Tooltip for used bar
      usedBar.setAttribute('data-tippy-content', `${d.categoryName}<br>Digunakan: ${formatNumber(d.used)}`);
      g.appendChild(usedBar);
      
      // Draw unused vouchers bar
      const unusedHeight = innerHeight - yScale(d.unused);
      const unusedBar = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      unusedBar.setAttribute('x', barX);
      unusedBar.setAttribute('y', innerHeight - usedHeight - unusedHeight);
      unusedBar.setAttribute('width', barWidth);
      unusedBar.setAttribute('height', unusedHeight);
      unusedBar.setAttribute('fill', colors.unused);
      unusedBar.setAttribute('rx', 2);
      
      // Tooltip for unused bar
      unusedBar.setAttribute('data-tippy-content', `${d.categoryName}<br>Belum digunakan: ${formatNumber(d.unused)}`);
      g.appendChild(unusedBar);
    });
    
    // Draw legend
    const legend = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    legend.setAttribute('transform', `translate(${innerWidth - 100}, 0)`);
    g.appendChild(legend);
    
    // Used legend item
    const usedRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    usedRect.setAttribute('x', 0);
    usedRect.setAttribute('y', 0);
    usedRect.setAttribute('width', 12);
    usedRect.setAttribute('height', 12);
    usedRect.setAttribute('fill', colors.used);
    usedRect.setAttribute('rx', 2);
    legend.appendChild(usedRect);
    
    const usedText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    usedText.setAttribute('x', 18);
    usedText.setAttribute('y', 9);
    usedText.setAttribute('dominant-baseline', 'middle');
    usedText.setAttribute('font-size', '10px');
    usedText.setAttribute('fill', '#6b7280');
    usedText.textContent = 'Digunakan';
    legend.appendChild(usedText);
    
    // Unused legend item
    const unusedRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    unusedRect.setAttribute('x', 0);
    unusedRect.setAttribute('y', 20);
    unusedRect.setAttribute('width', 12);
    unusedRect.setAttribute('height', 12);
    unusedRect.setAttribute('fill', colors.unused);
    unusedRect.setAttribute('rx', 2);
    legend.appendChild(unusedRect);
    
    const unusedText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    unusedText.setAttribute('x', 18);
    unusedText.setAttribute('y', 29);
    unusedText.setAttribute('dominant-baseline', 'middle');
    unusedText.setAttribute('font-size', '10px');
    unusedText.setAttribute('fill', '#6b7280');
    unusedText.textContent = 'Belum digunakan';
    legend.appendChild(unusedText);
  }
  
  // Pie chart for categories
  function drawCategoryPieChart(container, data) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    container.appendChild(svg);
    
    // Center of the pie chart
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(innerWidth, innerHeight) / 2;
    
    // Prepare data for pie chart
    const sortedData = [...data].sort((a, b) => b.total - a.total);
    const totalVouchers = sortedData.reduce((sum, d) => sum + d.total, 0);
    
    // Calculate angles for each slice
    let startAngle = 0;
    const slices = sortedData.map((d, i) => {
      const percentage = d.total / totalVouchers;
      const angle = percentage * Math.PI * 2;
      
      const slice = {
        data: d,
        startAngle,
        endAngle: startAngle + angle,
        percentage,
        color: colors.pie[i % colors.pie.length]
      };
      
      startAngle += angle;
      return slice;
    });
    
    // Draw pie slices
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(${centerX},${centerY})`);
    svg.appendChild(g);
    
    slices.forEach(slice => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      
      // Calculate path
      const startX = Math.cos(slice.startAngle) * radius;
      const startY = Math.sin(slice.startAngle) * radius;
      const endX = Math.cos(slice.endAngle) * radius;
      const endY = Math.sin(slice.endAngle) * radius;
      
      // Arc flag is 1 for slices less than 180 degrees, 0 for larger slices
      const largeArcFlag = slice.endAngle - slice.startAngle > Math.PI ? 1 : 0;
      
      const pathData = [
        `M 0 0`,
        `L ${startX} ${startY}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        'Z'
      ].join(' ');
      
      path.setAttribute('d', pathData);
      path.setAttribute('fill', slice.color);
      path.setAttribute('stroke', 'white');
      path.setAttribute('stroke-width', '1');
      
      // Tooltip
      path.setAttribute('data-tippy-content', 
        `${slice.data.categoryName}<br>` +
        `Jumlah: ${formatNumber(slice.data.total)}<br>` +
        `Persentase: ${formatPercentage(slice.percentage * 100)}`
      );
      
      g.appendChild(path);
    });
    
    // Draw legend
    const legendG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    legendG.setAttribute('transform', `translate(${centerX + radius + 20}, ${centerY - (slices.length * 15) / 2})`);
    svg.appendChild(legendG);
    
    slices.forEach((slice, i) => {
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', 0);
      rect.setAttribute('y', i * 15);
      rect.setAttribute('width', 10);
      rect.setAttribute('height', 10);
      rect.setAttribute('fill', slice.color);
      legendG.appendChild(rect);
      
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', 15);
      text.setAttribute('y', i * 15 + 8);
      text.setAttribute('font-size', '10px');
      text.setAttribute('fill', '#6b7280');
      const displayName = slice.data.categoryName.length > 20 
        ? slice.data.categoryName.substring(0, 20) + '...' 
        : slice.data.categoryName;
      text.textContent = `${displayName} (${formatPercentage(slice.percentage * 100)})`;
      legendG.appendChild(text);
    });
  }
  
  // Draw trend chart
  function drawTrendChart(container, data) {
    // Clear previous chart
    container.innerHTML = '';
    
    if (!data || data.length === 0) {
      container.innerHTML = '<div class="text-center text-gray-400">Tidak ada data tren</div>';
      return;
    }
    
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    container.appendChild(svg);
    
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(${margin.left},${margin.top})`);
    svg.appendChild(g);
    
    // Prepare data
    const maxValue = Math.max(...data.map(d => d.count), 1);
    
    // X scale - dates
    const xStep = innerWidth / (data.length - 1);
    const xScale = data.map((d, i) => ({
      value: d.date,
      position: i * xStep
    }));
    
    // Y scale - voucher counts
    const yScale = value => (innerHeight * (1 - value / maxValue));
    
    // Draw axes
    // X axis
    const xAxis = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    xAxis.setAttribute('transform', `translate(0,${innerHeight})`);
    g.appendChild(xAxis);
    
    // X axis line
    const xAxisLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    xAxisLine.setAttribute('x1', 0);
    xAxisLine.setAttribute('y1', 0);
    xAxisLine.setAttribute('x2', innerWidth);
    xAxisLine.setAttribute('y2', 0);
    xAxisLine.setAttribute('stroke', '#e5e7eb');
    xAxis.appendChild(xAxisLine);
    
    // X axis labels (show every 5th date to avoid overcrowding)
    xScale.forEach((x, i) => {
      if (i % 5 === 0 || i === data.length - 1) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', x.position);
        text.setAttribute('y', 20);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('font-size', '10px');
        text.setAttribute('fill', '#6b7280');
        
        // Format date as DD/MM
        const dateParts = x.value.split('-');
        text.textContent = `${dateParts[2]}/${dateParts[1]}`;
        
        xAxis.appendChild(text);
      }
    });
    
    // Y axis
    const yAxis = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.appendChild(yAxis);
    
    // Y axis line
    const yAxisLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    yAxisLine.setAttribute('x1', 0);
    yAxisLine.setAttribute('y1', 0);
    yAxisLine.setAttribute('x2', 0);
    yAxisLine.setAttribute('y2', innerHeight);
    yAxisLine.setAttribute('stroke', '#e5e7eb');
    yAxis.appendChild(yAxisLine);
    
    // Y axis ticks and labels
    const yTicks = 5;
    for (let i = 0; i <= yTicks; i++) {
      const value = maxValue * (i / yTicks);
      const y = yScale(value);
      
      // Tick line
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      tick.setAttribute('x1', -6);
      tick.setAttribute('y1', y);
      tick.setAttribute('x2', 0);
      tick.setAttribute('y2', y);
      tick.setAttribute('stroke', '#e5e7eb');
      yAxis.appendChild(tick);
      
      // Grid line
      const gridLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      gridLine.setAttribute('x1', 0);
      gridLine.setAttribute('y1', y);
      gridLine.setAttribute('x2', innerWidth);
      gridLine.setAttribute('y2', y);
      gridLine.setAttribute('stroke', '#f3f4f6');
      gridLine.setAttribute('stroke-dasharray', '2,2');
      g.appendChild(gridLine);
      
      // Label
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', -10);
      text.setAttribute('y', y);
      text.setAttribute('text-anchor', 'end');
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('font-size', '10px');
      text.setAttribute('fill', '#6b7280');
      text.textContent = formatNumber(Math.round(value));
      yAxis.appendChild(text);
    }
    
    // Draw area under the line
    let areaPath = `M ${xScale[0].position} ${innerHeight}`;
    
    data.forEach((d, i) => {
      const x = xScale[i].position;
      const y = yScale(d.count);
      areaPath += ` L ${x} ${y}`;
    });
    
    areaPath += ` L ${xScale[data.length - 1].position} ${innerHeight} Z`;
    
    const area = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    area.setAttribute('d', areaPath);
    area.setAttribute('fill', '#93c5fd');
    area.setAttribute('fill-opacity', '0.3');
    g.appendChild(area);
    
    // Draw line
    let linePath = '';
    
    data.forEach((d, i) => {
      const x = xScale[i].position;
      const y = yScale(d.count);
      
      if (i === 0) {
        linePath += `M ${x} ${y}`;
      } else {
        linePath += ` L ${x} ${y}`;
      }
    });
    
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    line.setAttribute('d', linePath);
    line.setAttribute('fill', 'none');
    line.setAttribute('stroke', '#3b82f6');
    line.setAttribute('stroke-width', '2');
    g.appendChild(line);
    
    // Draw data points
    data.forEach((d, i) => {
      const x = xScale[i].position;
      const y = yScale(d.count);
      
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', x);
      circle.setAttribute('cy', y);
      circle.setAttribute('r', 4);
      circle.setAttribute('fill', '#3b82f6');
      circle.setAttribute('stroke', 'white');
      circle.setAttribute('stroke-width', '1');
      
      // Tooltip
      circle.setAttribute('data-tippy-content', 
        `Tanggal: ${d.date}<br>` +
        `Voucher digunakan: ${formatNumber(d.count)}`
      );
      
      g.appendChild(circle);
    });
  }
  
  // Fetch analytics data
  // Fetch title options for filter
  async function fetchTitles() {
    try {
      loadingFilters = true;
      // Initialize titles as an empty array to avoid null errors
      titles = [];
      
      const response = await authFetch('/vouchers/titles');
      console.log('Titles response:', response);
      
      // Check all possible response formats based on server logs
      if (response && Array.isArray(response)) {
        titles = response;
        console.log('Loaded titles (array):', titles);
      } else if (response && Array.isArray(response.data)) {
        titles = response.data;
        console.log('Loaded titles from data property:', titles);
      } else if (response && response.success && Array.isArray(response.titles)) {
        // This is the format we're receiving: {success: true, titles: Array(1)}
        titles = response.titles;
        console.log('Loaded titles from titles property:', titles);
      } else {
        console.error('Failed to load titles, unexpected format:', response);
        titles = []; // Ensure we have an array even if the API call fails
      }
    } catch (error) {
      console.error('Error fetching titles:', error);
      titles = []; // Ensure we have an array even if the API call fails
    } finally {
      loadingFilters = false;
    }
  }
  
  // Fetch category options based on selected title
  async function fetchCategories() {
    try {
      loadingFilters = true;
      categories = [];
      selectedCategoryId = '';
      
      if (selectedTitleId) {
        const response = await authFetch(`/vouchers/categories/${selectedTitleId}`);
        console.log('Categories response:', response);
        
        // Check all possible response formats
        if (response && Array.isArray(response)) {
          categories = response;
          console.log('Loaded categories (array):', categories);
        } else if (response && Array.isArray(response.data)) {
          categories = response.data;
          console.log('Loaded categories from data property:', categories);
        } else if (response && response.success && Array.isArray(response.categories)) {
          // If server returns {success: true, categories: [...]}
          categories = response.categories;
          console.log('Loaded categories from categories property:', categories);
        } else if (response && response.success && Array.isArray(response.data)) {
          // If server returns {success: true, data: [...]}
          categories = response.data;
          console.log('Loaded categories from data property:', categories);
        } else {
          console.error('Failed to load categories, unexpected format:', response);
          categories = []; // Ensure we have an array even if the API call fails
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      categories = []; // Ensure we have an array even if the API call fails
    } finally {
      loadingFilters = false;
    }
  }
  
  // Handle title selection change
  function handleTitleChange() {
    fetchCategories();
    fetchAnalytics();
  }
  
  // Handle category selection change
  function handleCategoryChange() {
    fetchAnalytics();
  }
  
  // Reset filters
  function resetFilters() {
    selectedTitleId = '';
    selectedCategoryId = '';
    categories = [];
    fetchAnalytics();
  }
  
  async function fetchAnalytics() {
    try {
      loading = true;
      error = null;
      
      const response = await getVoucherAnalytics(selectedTitleId, selectedCategoryId);
      analytics = response;
      loading = false;
      
      // Draw charts after data is loaded
      setTimeout(() => {
        if (analytics) {
          const categoryChartContainer = document.getElementById('category-chart');
          const trendChartContainer = document.getElementById('trend-chart');
          
          if (categoryChartContainer) {
            drawCategoryChart(categoryChartContainer, analytics.categoryData);
          }
          
          if (trendChartContainer) {
            drawTrendChart(trendChartContainer, analytics.trendsData);
          }
        }
      }, 0);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      error = err.message || 'Gagal memuat data analitik';
      loading = false;
      showAlert('error', error);
    }
  }
  
  // Toggle chart type
  function toggleChartType() {
    chartType = chartType === 'bar' ? 'pie' : 'bar';
    
    // Redraw chart with new type
    const categoryChartContainer = document.getElementById('category-chart');
    if (categoryChartContainer && analytics) {
      drawCategoryChart(categoryChartContainer, analytics.categoryData);
    }
  }
  
  // Refresh data
  function refreshData() {
    fetchAnalytics();
  }
  
  onMount(() => {
    fetchTitles();
    fetchAnalytics();
  });
</script>

<div class="analytics-container">
  <div class="page-header">
    <h1>Dashboard Analitik Voucher</h1>
    <div class="header-actions">
      <button class="refresh-button" on:click={refreshData}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M23 4v6h-6"></path>
          <path d="M1 20v-6h6"></path>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
          <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
        </svg>
        Refresh
      </button>
    </div>
  </div>
  
  <div class="filters-container">
    <div class="filter-group">
      <label for="title-filter">Filter berdasarkan Judul:</label>
      <select 
        id="title-filter" 
        bind:value={selectedTitleId} 
        on:change={handleTitleChange}
        disabled={loadingFilters}
      >
        <option value="">Semua Judul</option>
        {#each titles as title}
          <option value={title._id}>{title.name}</option>
        {/each}
      </select>
    </div>
    
    <div class="filter-group">
      <label for="category-filter">Filter berdasarkan Kategori:</label>
      <select 
        id="category-filter" 
        bind:value={selectedCategoryId} 
        on:change={handleCategoryChange}
        disabled={loadingFilters || categories.length === 0}
      >
        <option value="">Semua Kategori</option>
        {#each categories as category}
          <option value={category._id}>{category.nominal.toLocaleString('id-ID')}</option>
        {/each}
      </select>
    </div>
    
    <button 
      class="reset-button" 
      on:click={resetFilters}
      disabled={!selectedTitleId && !selectedCategoryId}
    >
      Reset Filter
    </button>
  </div>
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Memuat data analitik...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p>Error: {error}</p>
      <button on:click={fetchAnalytics}>Coba lagi</button>
    </div>
  {:else if analytics}
    <div class="analytics-overview">
      <div class="stats-card">
        <div class="stats-icon voucher-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="6" width="20" height="12" rx="2"></rect>
            <path d="M22 10c-1.5 0-3 .5-3 2s1.5 2 3 2"></path>
            <path d="M2 10c1.5 0 3 .5 3 2s-1.5 2-3 2"></path>
            <path d="M12 10v4"></path>
            <path d="M12 14h4"></path>
          </svg>
        </div>
        <div class="stats-content">
          <h3>Total Voucher</h3>
          <p class="stats-value">{formatNumber(analytics.overview.totalVouchers)}</p>
        </div>
      </div>
      
      <div class="stats-card">
        <div class="stats-icon used-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        <div class="stats-content">
          <h3>Voucher Digunakan</h3>
          <p class="stats-value">{formatNumber(analytics.overview.usedVouchers)}</p>
        </div>
      </div>
      
      <div class="stats-card">
        <div class="stats-icon unused-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 8v4"></path>
            <path d="M12 16h.01"></path>
          </svg>
        </div>
        <div class="stats-content">
          <h3>Voucher Belum Digunakan</h3>
          <p class="stats-value">{formatNumber(analytics.overview.unusedVouchers)}</p>
        </div>
      </div>
      
      <div class="stats-card">
        <div class="stats-icon rate-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
          </svg>
        </div>
        <div class="stats-content">
          <h3>Tingkat Penggunaan</h3>
          <p class="stats-value">{formatPercentage(analytics.overview.usageRate)}</p>
        </div>
      </div>
    </div>
    
    <div class="analytics-charts">
      <div class="chart-container">
        <div class="chart-header">
          <h2>Penggunaan Voucher per Kategori</h2>
          <button class="chart-toggle-button" on:click={toggleChartType}>
            {chartType === 'bar' ? 'Tampilkan Pie Chart' : 'Tampilkan Bar Chart'}
          </button>
        </div>
        <div id="category-chart" class="chart-content"></div>
      </div>
      
      <div class="chart-container">
        <div class="chart-header">
          <h2>Tren Penggunaan Voucher (30 Hari Terakhir)</h2>
        </div>
        <div id="trend-chart" class="chart-content"></div>
      </div>
    </div>
  {/if}
</div>

<style>
  .analytics-container {
    padding: 0;
  }
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .page-header h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 0;
    letter-spacing: -0.025em;
  }
  
  .header-actions {
    display: flex;
    gap: 0.5rem;
  }
  
  .refresh-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #3b82f6;
    border: none;
    border-radius: 0.375rem;
    color: white;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .refresh-button:hover {
    background-color: #2563eb;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 0;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .loading-spinner {
    width: 3rem;
    height: 3rem;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1.25rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.5rem;
    background-color: #fee2e2;
    border-radius: 0.5rem;
    color: #b91c1c;
    text-align: center;
  }
  
  .error-container button {
    margin-top: 1.25rem;
    padding: 0.5rem 1.25rem;
    background-color: #dc2626;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .error-container button:hover {
    background-color: #b91c1c;
  }
  
  .analytics-overview {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.25rem;
    margin-bottom: 2.5rem;
  }
  
  .stats-card {
    display: flex;
    align-items: center;
    padding: 1.5rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s, box-shadow 0.2s;
  }
  
  .stats-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  
  .stats-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 0.75rem;
    margin-right: 1.25rem;
    color: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  .voucher-icon {
    background-color: #3b82f6;
    background-image: linear-gradient(135deg, #3b82f6, #60a5fa);
  }
  
  .used-icon {
    background-color: #10b981;
    background-image: linear-gradient(135deg, #10b981, #34d399);
  }
  
  .unused-icon {
    background-color: #ef4444;
    background-image: linear-gradient(135deg, #ef4444, #f87171);
  }
  
  .rate-icon {
    background-color: #f59e0b;
    background-image: linear-gradient(135deg, #f59e0b, #fbbf24);
  }
  
  .stats-content h3 {
    margin: 0 0 0.5rem 0;
    font-size: 0.9375rem;
    font-weight: 500;
    color: #4b5563;
  }
  
  .stats-value {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
  }
  
  .analytics-charts {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .chart-container {
    background-color: white;
    border-radius: 0.75rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    overflow: hidden;
  }
  
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .chart-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e3a8a;
    margin: 0;
  }
  
  .chart-toggle-button {
    padding: 0.5rem 1rem;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.375rem;
    color: #3b82f6;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .chart-toggle-button:hover {
    background-color: #dbeafe;
    border-color: #93c5fd;
  }
  
  .chart-content {
    width: 100%;
    display: flex;
    justify-content: center;
    overflow-x: auto;
    min-height: 400px;
  }
  
  @media (max-width: 768px) {
    .analytics-overview {
      grid-template-columns: 1fr 1fr;
    }
  }
  
  /* Filter styles */
  .filters-container {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .filter-group {
    flex: 1;
    min-width: 200px;
  }
  
  .filter-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4b5563;
  }
  
  .filter-group select {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    color: #1f2937;
    background-color: white;
  }
  
  .filter-group select:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
  
  .reset-button {
    align-self: flex-end;
    padding: 0.5rem 1rem;
    background-color: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    color: #4b5563;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .reset-button:hover:not(:disabled) {
    background-color: #e5e7eb;
  }
  
  .reset-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    .analytics-overview {
      grid-template-columns: 1fr;
    }
    
    .chart-header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .chart-toggle-button {
      margin-top: 0.75rem;
    }
    
    .filters-container {
      flex-direction: column;
    }
    
    .reset-button {
      align-self: flex-start;
      margin-top: 0.5rem;
    }
  }
</style>